import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type { MinifiedTask } from '../../../../../api/tasks-client/task-client.type';
import { WorkUnitsSortOption } from './side-section.type';

/**
 * Formats the work units count with proper pluralization
 *
 * @param {number} count - The number of work units to format
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} The formatted string with proper pluralization and count
 */
const formatWorkUnitsCount = (count: number, t: TFunction): string => {
  const key =
    count === 1
      ? 'process_analysis.work_units.work_units_count_singular'
      : 'process_analysis.work_units.work_units_count';

  return t(key, {
    count,
    work_units: t('common.work_units'),
    work_unit: t('common.work_unit'),
  });
};

/**
 * Formats points value with proper pluralization of estimation label
 *
 * @param {number | null} points - The points value to format
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {string} Formatted string with points and estimation label, or '-' if points is null/undefined
 */
const formatPoints = (points: number | null, t: TFunction): string => {
  if (points === null || points === undefined) {
    return '-';
  }

  if (points === 1) {
    return `${points} ${t('common.estimation_singular')}`;
  }

  return `${points} ${t('common.estimation')}`;
};

/**
 * Sorts work units based on the selected sort option
 *
 * @param {MinifiedTask[]} workUnits - The array of work units to sort
 * @param {WorkUnitsSortOption} sortBy - The sort option to use
 * @returns {MinifiedTask[]} The sorted array of work units
 */
const sortWorkUnits = (workUnits: MinifiedTask[], sortBy: WorkUnitsSortOption): MinifiedTask[] => {
  return [...workUnits].sort((a, b) => {
    switch (sortBy) {
      case WorkUnitsSortOption.CreatedDate:
        return dayjs(b.created_date).diff(dayjs(a.created_date));
      case WorkUnitsSortOption.Type:
        return a.type.localeCompare(b.type);
      case WorkUnitsSortOption.Points:
        if (a.points === null && b.points === null) return 0;
        if (a.points === null) return 1;
        if (b.points === null) return -1;
        return b.points - a.points;
      default:
        return 0;
    }
  });
};

export { formatPoints, formatWorkUnitsCount, sortWorkUnits };
